'use client';

import { Fragment, useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import NumSlotMan from '@publishing/assets/icons/events/ff-today/num_slot_man.svg';
import NumSlotWon from '@publishing/assets/icons/events/ff-today/num_slot_won.svg';
import NumTimerColon from '@publishing/assets/icons/events/ff-today/num_timer_colon.svg';

import {
  getPriceSlotColumns,
  getSlotSpinDurationMs,
  remainingUntilKstMidnight,
  SLOT_REPLAY_PAUSE_MS,
  splitHmsDigitGroups,
  TODAY_CONTACT_COPY,
  type KstMidnightCountdown,
} from './data';
import s from './today.module.scss';
import { NUM_SLOT_ICONS, NUM_TIMER_ICONS } from './todayIcons';

/** 자정 카운트다운과 슬롯 릴 재생. replayKey가 바뀌면 처음부터 다시 돈다. */
export function useTodaySlotPlayback(
  replayKey: string,
  autoReplay = true,
  replayAfterMs = getSlotSpinDurationMs() + SLOT_REPLAY_PAUSE_MS,
) {
  const [countdown, setCountdown] = useState(() => remainingUntilKstMidnight(Date.now()));
  const [spinId, setSpinId] = useState(0);

  useEffect(() => {
    const tick = () => setCountdown(remainingUntilKstMidnight(Date.now()));
    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (!autoReplay) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timerId = window.setTimeout(() => {
      setSpinId((current) => current + 1);
    }, replayAfterMs);
    return () => window.clearTimeout(timerId);
  }, [spinId, replayKey, autoReplay, replayAfterMs]);

  return { countdown, spinId };
}

export function TodayPriceSlot({
  priceManwon,
  spinKey,
}: {
  priceManwon: number;
  spinKey: string;
}) {
  const slotColumns = getPriceSlotColumns(priceManwon);

  return (
    <div key={spinKey} className={s.slot_box}>
      <div
        className={s.slot_box_inner}
        role="img"
        aria-label={TODAY_CONTACT_COPY.priceAriaLabel(priceManwon)}
      >
        {slotColumns.map((column) => (
          <div key={column.key} className={s.slot_column} aria-hidden="true">
            <div className={s.slot_reel}>
              <div
                className={s.slot_strip}
                style={
                  {
                    '--slot-travel': column.travel,
                    '--slot-delay': column.delay,
                    '--slot-duration': column.duration,
                  } as CSSProperties
                }
              >
                {column.type === 'digit'
                  ? column.digits.map((digit, index) => {
                      const Digit = NUM_SLOT_ICONS[digit];
                      return (
                        <div key={`${column.key}-${index}`} className={s.slot_strip_item}>
                          <Digit />
                        </div>
                      );
                    })
                  : column.units.map((unit, index) => (
                      <div key={`${column.key}-${index}`} className={s.slot_strip_item}>
                        {unit === 'man' ? <NumSlotMan /> : <NumSlotWon />}
                      </div>
                    ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** 시:분:초 숫자. 슬롯 하단·특가 섹션이 같은 자정 카운트다운을 쓴다. */
export function TodayClockDigits({
  countdown,
  className = s.slot_countdown_time,
  labelled = false,
}: {
  countdown: KstMidnightCountdown;
  className?: string;
  /** 옆에 시각 텍스트가 없으면 시계 자체에 이름을 붙인다. */
  labelled?: boolean;
}) {
  const timeGroups = splitHmsDigitGroups(countdown.digits);

  return (
    <div
      className={className}
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? countdown.label : undefined}
      role={labelled ? 'timer' : undefined}
    >
      {timeGroups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 ? <NumTimerColon className={s.slot_countdown_colon} /> : null}
          {group.split('').map((digit, digitIndex) => {
            const Digit = NUM_TIMER_ICONS[Number(digit)];
            return (
              <Digit
                key={`${groupIndex}-${digitIndex}`}
                className={digit === '1' ? s.slot_countdown_digit_1 : undefined}
              />
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}

export function TodayClock({ className = s.special_offer_clock }: { className?: string }) {
  const { countdown } = useTodaySlotPlayback('today-clock', false);
  return <TodayClockDigits countdown={countdown} className={className} labelled />;
}

export function TodayCountdown({
  countdown,
  text,
  timeSide = 'start',
}: {
  countdown: KstMidnightCountdown;
  text: ReactNode;
  /** start: 숫자 왼쪽. end: 숫자 오른쪽 */
  timeSide?: 'start' | 'end';
}) {
  const time = <TodayClockDigits countdown={countdown} />;
  const note = (
    <p className={s.slot_countdown_text}>
      <span className="blind">{countdown.label} </span>
      {text}
    </p>
  );

  return (
    <div className={s.slot_countdown}>
      <div className={s.slot_countdown_bar}>
        {timeSide === 'end' ? (
          <>
            {note}
            {time}
          </>
        ) : (
          <>
            {time}
            {note}
          </>
        )}
      </div>
    </div>
  );
}
